'use client';

import { useState } from 'react';
import { BookOpen, FileText, Globe, Link2, MapPin } from 'lucide-react';
import { Image } from '@/atoms/Image/Image';
import { PubkyIcon } from '@/icons';
import { getFaviconUrl } from './ResourceCard.utils';

const ICON_SIZE = 16;
const ICON_PROPS = { size: ICON_SIZE, 'aria-hidden': true, className: 'shrink-0 text-muted-foreground' } as const;

/** An icon for a URI that is not a web page: its scheme says what it is, there is no site to ask. */
function SchemeIcon({ uri }: { uri: string }) {
  const scheme = /^[a-z][a-z0-9+.-]*:/i.exec(uri)?.[0].toLowerCase();
  switch (scheme) {
    case 'isbn:':
      return <BookOpen {...ICON_PROPS} />;
    case 'doi:':
      return <FileText {...ICON_PROPS} />;
    case 'geo:':
      return <MapPin {...ICON_PROPS} />;
    case 'pubky:':
      return <PubkyIcon {...ICON_PROPS} />;
    default:
      return <Link2 {...ICON_PROPS} />;
  }
}

interface ResourceSourceIconProps {
  /** The link the source stands for. */
  uri: string;
}

/**
 * The site's own favicon (`https://<host>/favicon.ico`) for a web link, a globe when it fails to
 * load, and an icon for the kind of identifier (`isbn:`, `doi:`, `pubky://`) otherwise. Decorative:
 * the site name is always shown next to it.
 */
export function ResourceSourceIcon({ uri }: ResourceSourceIconProps) {
  const faviconUrl = getFaviconUrl(uri);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  if (faviconUrl && failedUrl !== faviconUrl) {
    return (
      <Image
        src={faviconUrl}
        alt=""
        width={ICON_SIZE}
        height={ICON_SIZE}
        data-testid="resource-source-favicon"
        className="size-4 shrink-0 rounded-sm"
        onError={() => setFailedUrl(faviconUrl)}
      />
    );
  }

  return faviconUrl ? <Globe {...ICON_PROPS} /> : <SchemeIcon uri={uri} />;
}
